import { useState } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

import ivoryHealthLogo from "@/assets/ivory-health-logo.webp";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { getTenantLogo } from "@/lib/tenantLogos";

export default function LoginPage() {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp, sendMobileOtp, verifyMobileOtp } = useAuth();
  const { name: tenantName, client_id: clientId, logo_url: logoUrl } = useTenant();
  const tenantLogo = getTenantLogo(clientId) || logoUrl;
  const [method, setMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("identity");
  const [error, setError] = useState("");
  // Only ever populated for accounts the backend explicitly flags as
  // review accounts — a normal sign-in never carries a code in its response.
  const [previewCode, setPreviewCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMobile = method === "mobile";
  const identity = isMobile ? mobile : email;

  function switchMethod(next) {
    setMethod(next);
    setError("");
    setCode("");
    setPreviewCode("");
  }

  async function submitIdentity(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      const response = await (isMobile ? sendMobileOtp(mobile) : sendOtp(email));
      setPreviewCode(response?.preview_otp || "");
      setStage("code");
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitCode(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      await (isMobile ? verifyMobileOtp(mobile, code) : verifyOtp(email, code));
      navigate("/");
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="login-page" data-testid="login-page" data-tenant={clientId}>
      <section className="login-hero" data-testid="login-hero">
        <div className="login-brand" data-testid="login-brand"><img src={ivoryHealthLogo} alt="Ivory Health" className="login-brand__logo" data-testid="ivory-brand-logo" /><strong>Ivory Health</strong></div>
        <p className="eyebrow">Employee wellbeing</p>
        <h1>Care that fits into your day.</h1>
        <p>Health checks, consultations, and other benefits for you and your family.</p>
      </section>
      <section className="login-panel" data-testid="login-panel">
        <div className="security-note" data-testid="secure-login-note"><ShieldCheck size={18} />Secure employee sign-in</div>
        {stage === "identity" ? (
          <form onSubmit={submitIdentity} className="login-form" data-testid="email-login-form">
            <h2>Your wellbeing, one login away</h2>
            <div className="login-method-switch" role="tablist" aria-label="Sign-in method">
              <button
                type="button"
                role="tab"
                aria-selected={!isMobile}
                className={!isMobile ? "is-active" : ""}
                onClick={() => switchMethod("email")}
                data-testid="login-method-email"
              >
                Work email
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isMobile}
                className={isMobile ? "is-active" : ""}
                onClick={() => switchMethod("mobile")}
                data-testid="login-method-mobile"
              >
                Mobile
              </button>
            </div>
            {isMobile ? (
              <>
                <p>Enter the mobile number registered with your HR team to receive a one-time code.</p>
                <label htmlFor="work-mobile">Mobile number</label>
                <input
                  id="work-mobile"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength="10"
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="9876543210"
                  required
                  data-testid="work-mobile-input"
                />
              </>
            ) : (
              <>
                <p>Enter your work email to receive a one-time verification code.</p>
                <label htmlFor="work-email">Work email</label>
                <input
                  id="work-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@company.com"
                  required
                  data-testid="work-email-input"
                />
              </>
            )}
            {error && <p className="form-error" role="alert" data-testid="login-error-message">{error}</p>}
            <Button type="submit" disabled={isSubmitting || (isMobile && mobile.length !== 10)} data-testid="request-otp-button">
              {isSubmitting ? "Preparing code…" : <>Continue <ArrowRight size={18} /></>}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="login-form" data-testid="otp-verification-form">
            <button className="text-button" type="button" onClick={() => { setStage("identity"); setCode(""); setError(""); setPreviewCode(""); }} data-testid="change-email-button">
              {isMobile ? "Use another number" : "Use another email"}
            </button>
            <h2>{isMobile ? "Check your messages" : "Check your inbox"}</h2>
            <p>A six-digit code was requested for <strong data-testid="verification-email">{identity}</strong>.</p>
            {previewCode && (
              <div className="login-preview-code" data-testid="login-preview-code">
                <span>Test account — your code is shown here instead of being emailed</span>
                <strong>{previewCode}</strong>
              </div>
            )}
            <label htmlFor="verification-code">Verification code</label>
            <input
              id="verification-code"
              inputMode="numeric"
              maxLength="6"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              required
              data-testid="otp-code-input"
            />
            {error && <p className="form-error" role="alert" data-testid="otp-error-message">{error}</p>}
            <Button type="submit" disabled={isSubmitting || code.length !== 6} data-testid="verify-otp-button">
              {isSubmitting ? "Verifying…" : "Let's go"}
            </Button>
          </form>
        )}
        <footer className="powered-by-footer" data-testid="powered-by-footer">
          {tenantLogo ? <img src={tenantLogo} alt={tenantName} data-testid="tenant-logo" /> : <span>{tenantName}</span>}
        </footer>
      </section>
    </main>
  );
}
